// Persistent image cache backed by IndexedDB.
// Stores image Blobs keyed by a stable cache key so that once an illustration
// has been fetched, it never needs to be re-downloaded — even across reloads
// or offline sessions. Returns object URLs ready to use in <img src=...>.

const DB_NAME = "kelas-image-cache";
const STORE = "images";
const VERSION = 1;
const MAX_ENTRIES = 300;
const TTL_MS = 1000 * 60 * 60 * 24 * 90; // 90 days

type Stored = {
  blob: Blob;
  url: string; // origin URL (for re-download if needed)
  ts: number;
};

let dbPromise: Promise<IDBDatabase> | null = null;

const openDB = (): Promise<IDBDatabase> => {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const s = db.createObjectStore(STORE, { keyPath: "key" });
        s.createIndex("ts", "ts");
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
};

const tx = async (mode: IDBTransactionMode) => {
  const db = await openDB();
  return db.transaction(STORE, mode).objectStore(STORE);
};

// In-memory mirror so we don't rebuild object URLs every render
const objectUrlCache = new Map<string, string>();

export async function getCachedImageUrl(key: string): Promise<string | null> {
  if (objectUrlCache.has(key)) return objectUrlCache.get(key)!;
  try {
    const store = await tx("readonly");
    const got: any = await new Promise((resolve, reject) => {
      const r = store.get(key);
      r.onsuccess = () => resolve(r.result);
      r.onerror = () => reject(r.error);
    });
    if (!got?.blob) return null;
    if (Date.now() - (got.ts || 0) > TTL_MS) return null;
    const url = URL.createObjectURL(got.blob as Blob);
    objectUrlCache.set(key, url);
    return url;
  } catch {
    return null;
  }
}

export async function cacheImageFromUrl(key: string, srcUrl: string): Promise<string | null> {
  try {
    // If we already have it, return the existing object URL
    const existing = await getCachedImageUrl(key);
    if (existing) return existing;

    const resp = await fetch(srcUrl, { mode: "cors", cache: "force-cache" });
    if (!resp.ok) return null;
    const blob = await resp.blob();

    const store = await tx("readwrite");
    await new Promise<void>((resolve, reject) => {
      const r = store.put({ key, blob, url: srcUrl, ts: Date.now() } as Stored & { key: string });
      r.onsuccess = () => resolve();
      r.onerror = () => reject(r.error);
    });

    // Best-effort LRU purge
    purge().catch(() => {});

    const objUrl = URL.createObjectURL(blob);
    objectUrlCache.set(key, objUrl);
    return objUrl;
  } catch {
    return null;
  }
}

async function purge() {
  const store = await tx("readwrite");
  const all: any[] = await new Promise((resolve, reject) => {
    const items: any[] = [];
    const r = store.openCursor();
    r.onsuccess = () => {
      const c = r.result;
      if (c) {
        items.push(c.value);
        c.continue();
      } else resolve(items);
    };
    r.onerror = () => reject(r.error);
  });
  // Drop expired
  const now = Date.now();
  for (const it of all) {
    if (now - (it.ts || 0) > TTL_MS) {
      try { (await tx("readwrite")).delete(it.key); } catch { /* ignore */ }
    }
  }
  // Enforce max entries (LRU by ts)
  const fresh = all.filter(it => now - (it.ts || 0) <= TTL_MS).sort((a, b) => a.ts - b.ts);
  if (fresh.length > MAX_ENTRIES) {
    const toDrop = fresh.slice(0, fresh.length - MAX_ENTRIES);
    for (const it of toDrop) {
      try { (await tx("readwrite")).delete(it.key); } catch { /* ignore */ }
    }
  }
}
